// hooks/useMilesOneDeepLink.tsx
import { useState, useEffect, useCallback, useRef } from "react";

export type DeepLinkStatus = "idle" | "attempting" | "fallback";

export default function useMilesOneDeepLink() {
  // UPDATED: Added webhook URL
  const appConfig = {
    customSchemes: {
      android: "milesoneandroid://open.miles.one",
      ios: "milesone://app",
    },
    appLinks: {
      ios: "https://www.mileseducation.com/milesone/app",
      android: "https://www.mileseducation.com/milesone/app",
    },
    appStoreLinks: {
      ios: "https://apps.apple.com/us/app/miles-us-pathway/id6504799221",
      android: "https://play.google.com/store/apps/details?id=com.miles.one",
    },
    // New webhook URL for handling fallbacks
    webhookRedirect: "https://webhook.miles-api.com/webhook/redirect-qr",
  };

  const [status, setStatus] = useState<DeepLinkStatus>("idle");
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);
  const attemptInProgress = useRef(false);

  // Device detection
  useEffect(() => {
    if (typeof window !== "undefined") {
      const userAgent = navigator.userAgent;
      setIsAndroid(/Android/i.test(userAgent));
      setIsIOS(/iPhone|iPad|iPod/i.test(userAgent));
    }
  }, []);

  const tryOpenApp = useCallback(() => {
    if (attemptInProgress.current) return;
    if (!isAndroid && !isIOS) return;

    console.log("Starting app deep link attempt");
    setStatus("attempting");
    attemptInProgress.current = true;

    let appLaunched = false;
    let storeRedirectTimer: ReturnType<typeof setTimeout> | null = null;

    // Define event handlers
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("App launch detected via visibility change");
        appLaunched = true;
        clearAllTimers();
        setStatus("idle");
      }
    };

    const handleBlur = () => {
      console.log("App launch detected via blur");
      appLaunched = true;
      clearAllTimers();
      setStatus("idle");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    function clearAllTimers() {
      if (storeRedirectTimer) {
        clearTimeout(storeRedirectTimer);
        storeRedirectTimer = null;
      }
    }

    function cleanUp() {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      attemptInProgress.current = false;
    }

    // LOOP PREVENTION FOR BOTH PLATFORMS
    const currentTime = new Date().getTime();
    const storageKey = isAndroid
      ? "android_deep_link_time"
      : "ios_deep_link_time";
    const lastAttemptTime = parseInt(localStorage.getItem(storageKey) || "0");
    const hasRecentlyAttempted = currentTime - lastAttemptTime < 3000;

    // Check if we're in a redirect loop on either platform
    if (hasRecentlyAttempted) {
      console.log(
        `Preventing ${isAndroid ? "Android" : "iOS"} redirect loop, using webhook redirect`,
      );
      localStorage.removeItem(storageKey);

      // Use webhook for all fallbacks
      window.location.replace(appConfig.webhookRedirect);

      cleanUp();
      return;
    }

    // Record this attempt for loop prevention
    localStorage.setItem(storageKey, currentTime.toString());

    // iOS IMPLEMENTATION - WITH WEBHOOK FALLBACK
    if (isIOS) {
      console.log("iOS device detected");

      // Detect if using Chrome on iOS
      const isIOSChrome = /CriOS/i.test(navigator.userAgent);
      console.log("Is Chrome on iOS:", isIOSChrome);

      // CHROME ON iOS
      if (isIOSChrome) {
        console.log("Chrome iOS detected");

        // Attempt iframe method first (works better for Chrome iOS)
        try {
          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          iframe.src = appConfig.customSchemes.ios;
          document.body.appendChild(iframe);

          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 500);
        } catch (e) {
          console.error("Iframe creation failed:", e);
        }

        // Also try custom scheme after a delay
        setTimeout(() => {
          if (!appLaunched) {
            console.log("Trying custom scheme for Chrome iOS");
            window.location.href = appConfig.customSchemes.ios;
          }
        }, 600);

        // Skip Universal Links for Chrome iOS (they often cause issues)
        // Instead go directly to App Store or webhook after sufficient delay
        storeRedirectTimer = setTimeout(() => {
          if (!appLaunched) {
            console.log(
              "App launch failed on Chrome iOS, using webhook redirect",
            );
            window.location.replace(appConfig.webhookRedirect);
            cleanUp();
          }
        }, 1600);
      }
      // SAFARI AND OTHER iOS BROWSERS
      else {
        console.log("Safari iOS detected");

        // Try Universal Links first
        window.location.href = appConfig.appLinks.ios;

        // Try custom scheme after a delay
        setTimeout(() => {
          if (!appLaunched) {
            console.log("Universal Link didn't launch, trying custom scheme");
            window.location.href = appConfig.customSchemes.ios;
          }
        }, 1000);

        // USE WEBHOOK if app doesn't open
        storeRedirectTimer = setTimeout(() => {
          if (!appLaunched) {
            console.log(
              "App launch failed on Safari iOS, using webhook redirect",
            );
            window.location.replace(appConfig.webhookRedirect);
            cleanUp();
          }
        }, 2000);
      }
    }
    // ANDROID IMPLEMENTATION - UPDATED WITH WEBHOOK FALLBACK
    else if (isAndroid) {
      console.log("Android device detected, using multiple methods");

      // SKIP App Links approach which causes the infinite loop
      // Instead start with intent format which works better
      window.location.href = `intent://open.miles.one#Intent;scheme=milesoneandroid;package=com.miles.one;end;`;

      // Direct scheme (works on some browsers)
      setTimeout(() => {
        if (!appLaunched) {
          console.log("Intent didn't work, trying direct scheme");
          window.location.href = appConfig.customSchemes.android;
        }
      }, 1000);

      // USE WEBHOOK as fallback
      storeRedirectTimer = setTimeout(() => {
        if (!appLaunched) {
          console.log("All attempts failed, using webhook redirect");
          window.location.replace(appConfig.webhookRedirect);
          cleanUp();
        }
      }, 2000);
    }

    // Clear localStorage flags after delay
    setTimeout(() => {
      localStorage.removeItem(storageKey);
    }, 5000);

    // Final cleanup for UI state
    setTimeout(() => {
      if (!appLaunched) {
        setStatus("fallback");
      }
      cleanUp();
    }, 3000);

    // Return cleanup function
    return cleanUp;
  }, [isAndroid, isIOS]);

  // Handle redirects if needed
  useEffect(() => {
    if (shouldRedirect) {
      setTimeout(() => {
        window.location.replace(shouldRedirect);
      }, 500);
    }
  }, [shouldRedirect]);

  return { status, tryOpenApp, isAndroid, isIOS, shouldRedirect };
}
